import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { usePublicAuth } from '../contexts/PublicAuthContext.jsx';

const toMillis = value => {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (value.seconds) {
    return value.seconds * 1000 + (value.nanoseconds || 0) / 1_000_000;
  }
  if (value.toDate && typeof value.toDate === 'function') {
    return value.toDate().getTime();
  }
  return null;
};

export function useWalletData(enabled = true) {
  const { user, initializing } = usePublicAuth();
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [accountDoc, setAccountDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;

    if (!user?.uid) {
      setBalance(0);
      setTransactions([]);
      setAccountDoc(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const userRef = doc(db, 'users', user.uid);
    const unsubscribeUser = onSnapshot(
      userRef,
      snapshot => {
        const data = snapshot.data() || {};
        setAccountDoc(data);
        setBalance(Number(data.walletBalance || 0));
        setLoading(false);
      },
      err => {
        console.error('Wallet user snapshot error:', err);
        setError(err.message);
        setLoading(false);
      },
    );

    const transactionsQuery = query(
      collection(db, 'wallet_transactions', user.uid, 'transactions'),
      orderBy('date', 'desc'),
    );

    const unsubscribeTransactions = onSnapshot(
      transactionsQuery,
      snapshot => {
        const mapped = snapshot.docs.map(docSnap => {
          const data = docSnap.data() || {};
          return {
            id: docSnap.id,
            ...data,
            date: toMillis(data.date) || Date.now(),
          };
        });
        setTransactions(mapped);
        setLoading(false);
      },
      err => {
        console.error('Wallet transactions snapshot error:', err);
        setError(err.message);
        setLoading(false);
      },
    );

    return () => {
      unsubscribeUser();
      unsubscribeTransactions();
    };
  }, [user?.uid, enabled]);

  const derived = useMemo(() => {
    const depositTotals = { count: 0, amount: 0, lastUsed: null };
    const withdrawalTotals = { count: 0, amount: 0, lastUsed: null };

    transactions.forEach(tx => {
      if (tx.type === 'deposit') {
        depositTotals.count += 1;
        depositTotals.amount += Math.abs(Number(tx.amount) || 0);
        depositTotals.lastUsed =
          depositTotals.lastUsed || tx.date || Date.now();
      } else if (tx.type === 'withdrawal') {
        withdrawalTotals.count += 1;
        withdrawalTotals.amount += Math.abs(Number(tx.amount) || 0);
        withdrawalTotals.lastUsed =
          withdrawalTotals.lastUsed || tx.date || Date.now();
      }
    });

    return { depositTotals, withdrawalTotals };
  }, [transactions]);

  return {
    user,
    initializing,
    balance,
    transactions,
    accountDoc,
    loading,
    error,
    depositTotals: derived.depositTotals,
    withdrawalTotals: derived.withdrawalTotals,
  };
}

export default useWalletData;


